import { useId } from "react";

import { type EmblemPaint, TierBadgeEmblem, type TierBadgeEmblemName } from "./tierBadgeEmblems";
import { deriveTierBadgeColors } from "./tierBadgePalette";

// Kept identical to the Admin app's `modules/loyaltyProgram/TierBadgeMedallion.tsx`.

export type TierBadgeSize = "xs" | "sm" | "md" | "lg";

/** Pixel size per badge size: chips, list rows, cards and profiles, the editor and patient sheet. */
export const TIER_BADGE_PX = { xs: 20, sm: 28, md: 46, lg: 80 } as const satisfies Record<
  TierBadgeSize,
  number
>;

/**
 * Per size, how much of the emblem is drawn and how thick its outline is in the 48-unit box, so
 * the outline lands near one screen pixel at every size.
 */
const SIZE_STYLE = {
  xs: { detail: "chip", edgeWidth: 2.2 },
  sm: { detail: "small", edgeWidth: 1.5 },
  md: { detail: "full", edgeWidth: 1 },
  lg: { detail: "full", edgeWidth: 0.75 },
} as const satisfies Record<TierBadgeSize, Pick<EmblemPaint, "detail" | "edgeWidth">>;

type TierBadgeMedallionProps = {
  readonly colorHex: string;
  readonly emblem: TierBadgeEmblemName;
  /** The letter the `Initial` emblem engraves. */
  readonly initial: string;
  readonly size: TierBadgeSize;
  readonly className?: string;
};

/**
 * A tier's badge as a piece of award insignia: the emblem's own silhouette, cut into facets that
 * catch the light from the top left like polished metal or a gem. Every colour comes from the
 * tier's one colour.
 */
export const TierBadgeMedallion = ({
  colorHex,
  emblem,
  initial,
  size,
  className,
}: TierBadgeMedallionProps) => {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const colors = deriveTierBadgeColors(colorHex);
  const px = TIER_BADGE_PX[size];
  const style = SIZE_STYLE[size];
  const metalId = `tier-metal-${id}`;
  const lustreId = `tier-lustre-${id}`;
  const shadowId = `tier-shadow-${id}`;
  const isDetailed = style.detail === "full";

  const paint: EmblemPaint = {
    tones: {
      hi: colors.rimLight,
      lit: colors.faceLight,
      base: colors.face,
      shade: colors.faceShade,
      deep: colors.rimShade,
    },
    metal: `url(#${metalId})`,
    lustre: `url(#${lustreId})`,
    edge: colors.edge,
    edgeWidth: style.edgeWidth,
    shine: colors.ring,
    ink: colors.emblem,
    inkDepth: colors.emblemDepth,
    detail: style.detail,
  };

  return (
    <svg
      aria-hidden
      className={["shrink-0 overflow-visible", className].filter(Boolean).join(" ")}
      data-tier-badge={emblem}
      data-tier-badge-detail={style.detail}
      data-tier-badge-size={size}
      data-tier-color={colors.face}
      data-tier-ink={colors.emblem}
      focusable="false"
      height={px}
      viewBox="0 0 48 48"
      width={px}
    >
      <defs>
        <linearGradient id={metalId} x1="0.1" x2="0.9" y1="0" y2="1">
          <stop offset="0" stopColor={colors.faceLight} />
          <stop offset="0.5" stopColor={colors.face} />
          <stop offset="1" stopColor={colors.faceShade} />
        </linearGradient>
        <radialGradient cx="0.36" cy="0.34" id={lustreId} r="0.78">
          <stop offset="0" stopColor={colors.glint} />
          <stop offset="0.45" stopColor={colors.rimLight} />
          <stop offset="0.85" stopColor={colors.face} />
          <stop offset="1" stopColor={colors.faceShade} />
        </radialGradient>
        {isDetailed && (
          <filter height="140%" id={shadowId} width="140%" x="-20%" y="-15%">
            <feDropShadow
              dx="0"
              dy="1"
              floodColor={colors.edge}
              floodOpacity="0.3"
              stdDeviation="0.9"
            />
          </filter>
        )}
      </defs>
      <g filter={isDetailed ? `url(#${shadowId})` : undefined}>
        <TierBadgeEmblem initial={initial} name={emblem} paint={paint} />
      </g>
    </svg>
  );
};
